import React from "react"
import { motion } from "framer-motion"
import { Shield, Plane, Heart, Briefcase, Users, AlertCircle, Phone, CheckCircle, Globe, GraduationCap } from "lucide-react"
import Layout from "../../components/common/Layout"
import SEO from "../../components/common/SEO"
import "./ServicePage.css"

const TravelInsurance = () => {
  const insurancePlans = [
    { icon: <GraduationCap />, title: "Student Plan", desc: "Specialized coverage for international students with medical up to $100k." },
    { icon: <Globe />, title: "Comprehensive", desc: "Complete protection for global travelers against trip cancellation and loss." },
    { icon: <Users />, title: "Family Plan", desc: "Affordable collective coverage for families with child-specific benefits." },
    { icon: <Briefcase />, title: "Business Elite", desc: "Professional protection for equipment and conference related contingencies." }
  ]

  const coverageItems = [
    { icon: <Heart color="var(--purple-glow)" />, title: "Medical Emergency", desc: "Hospitalization, surgery, and emergency outpatient care." },
    { icon: <AlertCircle color="var(--purple-glow)" />, title: "Trip Cancellation", desc: "Reimbursement for non-refundable costs due to disruptions." },
    { icon: <Plane color="var(--purple-glow)" />, title: "Baggage Protection", desc: "Coverage for lost, stolen, or damaged personal belongings." },
    { icon: <Shield color="var(--purple-glow)" />, title: "Personal Liability", desc: "Legal protection during your stay in a foreign country." }
  ]

  return (
    <Layout>
      <SEO
        title="Travel Insurance - Student & Global Coverage | Welsh Education Group"
        description="Comprehensive travel insurance for students. Medical, trip delay, and emergency assistance."
        url="/services/insurance"
      />

      <div className="service-page">
        <section className="service-hero-modern">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              className="hero-icon-large"
            >
              <Shield size={40} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Travel Insurance
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Secure your international journey with premium coverage and 24/7 global emergency assistance.
            </motion.p>
          </div>
        </section>

        <section className="service-content-modern">
          <div className="container">
            <div className="service-grid-modern">
              <div className="main-service-info">
                <h2>Total Peace of Mind</h2>
                <p>
                  Traveling abroad involves risks, from minor inconveniences like flight delays to major emergencies. Our insurance partners provide global network support, ensuring you receive world-class medical care and logistical help whenever you need it.
                </p>

                <div className="service-features-list">
                  {coverageItems.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="feature-pill"
                    >
                      <div className="feature-pill-icon">{item.icon}</div>
                      <div className="feature-pill-content">
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="plans-section" style={{ marginTop: '80px' }}>
                  <h2 style={{ marginBottom: '40px' }}>Curated Plans</h2>
                  <div className="info-grid">
                    {insurancePlans.map((plan, idx) => (
                      <div key={idx} className="info-card">
                        <h3>{plan.title}</h3>
                        <p style={{ color: 'var(--muted-foreground)', fontSize: '14px' }}>{plan.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="service-sidebar-modern">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  className="sidebar-cta-card"
                >
                  <Phone className="text-gradient" size={48} style={{ margin: '0 auto 24px' }} />
                  <h3>Emergency Support</h3>
                  <p>Global helpline available 24/7 for immediate aid.</p>
                  <button className="sidebar-btn highlight-glow">+1-800-WELSH-AID</button>
                </motion.div>

                <div className="info-card">
                  <h3 style={{ fontSize: '18px', marginBottom: '20px' }}>Global Network</h3>
                  <div className="stats-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <CheckCircle size={14} color="var(--cyan-glow)" /> <span style={{ fontSize: '14px' }}>Cashless Treatment</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <CheckCircle size={14} color="var(--cyan-glow)" /> <span style={{ fontSize: '14px' }}>99% Claim Settlement</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <CheckCircle size={14} color="var(--cyan-glow)" /> <span style={{ fontSize: '14px' }}>Instant Digital Policy</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default TravelInsurance
